import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Youtube, Instagram, ChevronRight, MapPin, Phone, Mail, Heart } from "lucide-react";

export const Footer = () => {
  return (
    <footer className="relative bg-gradient-to-br from-[#2C3E50] via-[#34495E] to-[#1A252F] text-white overflow-hidden" data-testid="footer">
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#E67E22] rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#F39C12] rounded-full filter blur-3xl"></div>
      </div>
      
      {/* Animated pattern overlay */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Logo and tagline */}
          <div className="space-y-6 animate-fade-in-up">
            <div className="group">
              <div className="mb-4 bg-white backdrop-blur-sm p-4 rounded-2xl inline-block transition-all duration-300 hover:bg-white hover:scale-105 hover:shadow-2xl shadow-lg">
                <img 
                  src="/assets/logo.png" 
                  alt="Wyldwoods Rural Retreat" 
                  className="h-16 transition-transform duration-300 group-hover:scale-110"
                />
              </div>
            </div>
            <p className="text-gray-300 text-base leading-relaxed">
              Where Nature Meets Luxury, and Every Stay Becomes a Story
            </p>
            <div className="flex space-x-3">
              {[
                { icon: Facebook, label: "Facebook" },
                { icon: Twitter, label: "Twitter" },
                { icon: Youtube, label: "YouTube" },
                { icon: Instagram, label: "Instagram" }
              ].map((social, index) => (
                <a 
                  key={social.label}
                  href="#" 
                  className="group relative w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center transition-all duration-300 hover:bg-gradient-to-r hover:from-[#E67E22] hover:to-[#F39C12] hover:-translate-y-1 hover:shadow-lg animate-fade-in-up" 
                  data-testid={`social-${social.label.toLowerCase()}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <social.icon size={20} className="text-gray-300 group-hover:text-white transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Contact Us */}
          <div className="animate-fade-in-up delay-200">
            <h3 className="font-serif text-2xl font-bold mb-6 flex items-center">
              <span className="w-1 h-8 bg-gradient-to-b from-[#E67E22] to-[#F39C12] rounded-full mr-3"></span>
              Contact Us
            </h3>
            <div className="space-y-4">
              <div className="flex items-start group">
                <div className="w-10 h-10 rounded-lg bg-[#E67E22]/20 flex items-center justify-center mr-3 group-hover:bg-[#E67E22] transition-colors duration-300">
                  <MapPin size={18} className="text-[#E67E22] group-hover:text-white transition-colors" />
                </div>
                <div className="text-gray-300 text-sm flex-1">
                  <p className="font-semibold text-white mb-1">WyldWoods Rural Retreat: 61</p>
                  <p>Puthiamman Street, Panaiyur</p>
                  <p>East Coast Road, Edaikazhinadu - 603302</p>
                </div>
              </div>
              
              <div className="flex items-center group">
                <div className="w-10 h-10 rounded-lg bg-[#E67E22]/20 flex items-center justify-center mr-3 group-hover:bg-[#E67E22] transition-colors duration-300">
                  <Phone size={18} className="text-[#E67E22] group-hover:text-white transition-colors" />
                </div>
                <a href="tel:+919445771282" className="text-gray-300 text-sm hover:text-white transition-colors">
                  +91 94457 71282
                </a>
              </div>
              
              <div className="flex items-center group">
                <div className="w-10 h-10 rounded-lg bg-[#E67E22]/20 flex items-center justify-center mr-3 group-hover:bg-[#E67E22] transition-colors duration-300">
                  <Mail size={18} className="text-[#E67E22] group-hover:text-white transition-colors" />
                </div>
                <a 
                  href="mailto:support@wyldwoods.in" 
                  className="text-[#F39C12] hover:text-[#E67E22] transition-colors font-medium"
                >
                  support@wyldwoods.in
                </a>
              </div>
            </div>
          </div>

          {/* Policy */}
          <div className="animate-fade-in-up delay-300">
            <h3 className="font-serif text-2xl font-bold mb-6 flex items-center">
              <span className="w-1 h-8 bg-gradient-to-b from-[#E67E22] to-[#F39C12] rounded-full mr-3"></span>
              Policy
            </h3>
            <ul className="space-y-3">
              {[
                { name: "Terms & Conditions", link: "/terms-and-conditions" },
                { name: "Shipping Policy", link: "/shipping-policy" },
                { name: "Privacy Policy", link: "/privacy-policy" },
                { name: "Cancellation & Refund Policy", link: "/cancellation-refund-policy" },
              ].map((policy, index) => (
                <li key={policy.name}>
                  <Link
                    to={policy.link}
                    className="group flex items-center text-gray-300 text-sm hover:text-white transition-all duration-300 animate-fade-in-up"
                    style={{ animationDelay: `${(index + 1) * 100}ms` }}
                  >
                    <div className="w-6 h-6 rounded-md bg-[#E67E22]/20 flex items-center justify-center mr-3 group-hover:bg-[#E67E22] transition-all duration-300 group-hover:scale-110">
                      <ChevronRight size={14} className="text-[#E67E22] group-hover:text-white transition-colors" />
                    </div>
                    <span className="underline-effect">{policy.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider with gradient */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/30 to-transparent mb-8"></div>

        {/* Copyright */}
        <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          <p className="text-gray-400 text-sm flex items-center">
            All Rights Reserved. © WYLDWOODS RURAL RETREAT {new Date().getFullYear()}
          </p>
          <p className="text-gray-400 text-sm flex items-center">
            Made with <Heart size={16} className="mx-2 text-[#E67E22] animate-pulse" fill="#E67E22" /> <a href="https://www.victinet.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[#E67E22] transition-colors underline-effect">for Nature Lovers</a>
          </p>
        </div>
      </div>

      {/* Scroll to top button - Hidden */}
      {/* <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-8 right-8 w-14 h-14 bg-gradient-to-r from-[#E67E22] to-[#F39C12] text-white rounded-full flex items-center justify-center hover:shadow-glow transition-all duration-300 hover:scale-110 group z-50"
        data-testid="scroll-to-top"
        aria-label="Scroll to top"
      >
        <ChevronRight size={28} className="rotate-[-90deg] group-hover:animate-bounce" />
      </button> */}
    </footer>
  );
};

export default Footer;